import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Container,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from 'reactstrap';
import PageBreadcrumb from '../../Components/Common/PageBreadcrumb';
import ActionButton from '../../Components/Common/ActionButton';
import { DetailBackButton } from '../../Components/Common/Detail';
import StatusBadge from '../../Components/Common/StatusBadge';
import Can from '../../Components/Common/Can';
import { EmptyState, SkeletonRows } from '../../Components/Common/ListStates';
import notify from '../../utils/notify';
import { confirmAction, confirmSubmit } from '../../utils/confirm';
import '../TenderOpening/components/TenderOpening.css';
import './components/GtcView.css';
import './components/GtcDetailView.css';
import {
  FormGrid,
  SelectField,
  TextAreaField,
  TextField,
  focusFirstError,
  hasErrors,
  numberBetween,
  required,
  validateForm,
} from '../../Components/Form';
import {
  MONTHS,
  YEARS,
  PUBLISH_TONE,
  STATUS_PUBLISH,
  STATUS_TONE,
  gtcApi,
  monthName,
  publishLabel,
} from './data/gtcApi';
import {
  articleLabel,
  nextSortOrder,
  sortByOrder,
} from '../../Components/Gtc/gtcAmendSummary';

const ORDER_RULES = [required(), numberBetween(1, 999)];

// Per-level modal copy. One Modal is reused for the three node types.
const MODAL_META = {
  category: {
    icon: 'ri-folder-2-line',
    addTitle: 'Add New Category',
    editTitle: 'Edit Category / Part',
    subtitle: 'Categories appear as headings in the GTC structure',
    saveLabel: 'Save Category',
    schema: {
      name: { label: 'Category Name', rules: [required()] },
      sortOrder: { label: 'Order', rules: ORDER_RULES },
    },
    order: ['name', 'sortOrder'],
    notifyAdd: 'Category added successfully.',
    notifyEdit: 'Category updated successfully.',
  },
  article: {
    icon: 'ri-article-line',
    addTitle: 'Add Article',
    editTitle: 'Edit Article',
    subtitle: 'Articles appear under a category',
    saveLabel: 'Save Article',
    schema: {
      number: { label: 'Number', rules: [required()] },
      title: { label: 'Article Title', rules: [required()] },
      sortOrder: { label: 'Order', rules: ORDER_RULES },
    },
    order: ['number', 'title', 'sortOrder'],
    notifyAdd: 'Article added successfully.',
    notifyEdit: 'Article updated successfully.',
  },
  clause: {
    icon: 'ri-file-list-3-line',
    addTitle: 'Add New Clause',
    editTitle: 'Edit Clause',
    subtitle: 'Clauses appear as the content of an article',
    saveLabel: 'Save Clause',
    schema: {
      clauseNumber: { label: 'Clause Number', rules: [required()] },
      content: { label: 'Clause Content', rules: [required()] },
      sortOrder: { label: 'Order', rules: ORDER_RULES },
    },
    order: ['clauseNumber', 'content', 'sortOrder'],
    notifyAdd: 'Clause added successfully.',
    notifyEdit: 'Clause updated successfully.',
  },
};

const EMPTY_NODE = {
  name: '',
  number: '',
  title: '',
  clauseNumber: '',
  content: '',
  sortOrder: '',
};

const YEAR_OPTIONS = YEARS.map(year => ({ value: year, label: year }));
const MONTH_OPTIONS = MONTHS.map((month, i) => ({
  value: String(i + 1),
  label: month,
}));

const isNotFound = err => err?.response?.status === 404 || err?.status === 404;

const GtcDetailView = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [gtc, setGtc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [expanded, setExpanded] = useState(() => new Set());

  const [modalOpen, setModalOpen] = useState(false);
  const [modalType, setModalType] = useState('category');
  const [parentId, setParentId] = useState(null);
  const [form, setForm] = useState(EMPTY_NODE);
  const [editingId, setEditingId] = useState(null);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  const [copyOpen, setCopyOpen] = useState(false);
  const [copyForm, setCopyForm] = useState({});

  document.title = `GTC Content - ${gtc?.title || id}`;

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const data = await gtcApi.getById(id);
      setGtc(data || null);
      setNotFound(!data);
    } catch (err) {
      if (isNotFound(err)) setNotFound(true);
      else notify.apiError(err, 'load GTC');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const tree = sortByOrder(gtc?.categories);
  const status = gtc?.publishStatus;
  const editable =
    gtc != null && (gtc.canEditContent ?? status !== STATUS_PUBLISH.PUBLISH);

  /* ------------------------------ expand -------------------------------- */

  const toggleNode = nodeId => {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(nodeId)) next.delete(nodeId);
      else next.add(nodeId);
      return next;
    });
  };

  /* ---------------------------- add / edit ------------------------------ */

  const openAdd = (type, parent = null, siblings = tree) => {
    setModalType(type);
    setParentId(parent);
    setForm({ ...EMPTY_NODE, sortOrder: String(nextSortOrder(siblings)) });
    setEditingId(null);
    setErrors({});
    setModalOpen(true);
  };

  const openEdit = (type, node) => {
    setModalType(type);
    setParentId(null);
    setForm({
      ...EMPTY_NODE,
      name: node.name || '',
      number: node.number || '',
      title: node.title || '',
      clauseNumber: node.clauseNumber || '',
      content: node.content || '',
      sortOrder: String(node.sortOrder ?? ''),
    });
    setEditingId(node.id);
    setErrors({});
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setErrors({});
  };

  const handleChange = e => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
    setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const handleSave = async () => {
    const meta = MODAL_META[modalType];
    const nextErrors = validateForm(form, meta.schema);
    setErrors(nextErrors);
    if (hasErrors(nextErrors)) {
      focusFirstError(nextErrors, meta.order);
      return;
    }
    const sortOrder = Number(form.sortOrder);
    const body = {
      category: { name: form.name.trim(), sortOrder },
      article: {
        number: form.number.trim(),
        title: form.title.trim(),
        sortOrder,
      },
      clause: {
        clauseNumber: form.clauseNumber.trim(),
        content: form.content.trim(),
        sortOrder,
      },
    }[modalType];
    const call = {
      category: editingId
        ? () => gtcApi.updateCategory(editingId, body)
        : () => gtcApi.createCategory(id, body),
      article: editingId
        ? () => gtcApi.updateArticle(editingId, body)
        : () => gtcApi.createArticle(parentId, body),
      clause: editingId
        ? () => gtcApi.updateClause(editingId, body)
        : () => gtcApi.createClause(parentId, body),
    }[modalType];

    setSaving(true);
    try {
      await call();
      notify.success(editingId ? meta.notifyEdit : meta.notifyAdd);
      if (parentId) setExpanded(prev => new Set([...prev, parentId]));
      setModalOpen(false);
      loadData();
    } catch (err) {
      notify.apiError(err, 'save GTC content');
    } finally {
      setSaving(false);
    }
  };

  /* ------------------------------ delete -------------------------------- */

  const runDelete = async (fn, message) => {
    try {
      await fn();
      notify.success(message);
      loadData();
    } catch (err) {
      notify.apiError(err, 'delete GTC content');
    }
  };

  /* ---------------------------- publication ----------------------------- */

  const runState = async (fn, message) => {
    try {
      await fn(gtc.id);
      notify.success(message);
      loadData();
    } catch (err) {
      notify.apiError(err, 'change GTC status');
    }
  };

  const handlePublish = async () => {
    const ok = await confirmSubmit('GTC', gtc.title, {
      title: 'Publish this GTC to Registered Supplier?',
      message:
        'Content will be locked after publishing. Use Copy as New Version for revisions.',
      confirmLabel: 'Publish GTC',
    });
    if (ok) runState(gtcApi.publish, 'GTC published to Registered Supplier.');
  };

  const handleUnpublish = async () => {
    const ok = await confirmAction({
      tone: 'warning',
      title: 'Unpublish this GTC?',
      message: 'Registered Supplier will no longer see this GTC.',
      subject: gtc.title,
      confirmLabel: 'Unpublish GTC',
    });
    if (ok) runState(gtcApi.unpublish, 'GTC unpublished.');
  };

  const handleSetDraft = async () => {
    const ok = await confirmAction({
      tone: 'warning',
      title: 'Set this GTC to Draft?',
      message: 'The GTC returns to Draft and its content can be edited again.',
      subject: gtc.title,
      confirmLabel: 'Set to Draft',
    });
    if (ok) runState(gtcApi.setDraft, 'GTC set to Draft.');
  };

  /* ------------------------------- copy --------------------------------- */

  const openCopy = () => {
    setCopyForm({
      title: gtc.title || '',
      year: String(gtc.year || new Date().getFullYear()),
      month: String(gtc.month || 1),
      description: gtc.description || '',
    });
    setCopyOpen(true);
  };

  const handleCopy = async () => {
    const ok = await confirmAction({
      tone: 'primary',
      title: 'Copy as new version?',
      message:
        'A new Draft GTC is created with all categories, articles and clauses.',
      subject: gtc.title,
      confirmLabel: 'Copy GTC',
    });
    if (!ok) return;
    setSaving(true);
    try {
      const created = await gtcApi.copy(gtc.id, {
        title: copyForm.title.trim(),
        year: Number(copyForm.year),
        month: Number(copyForm.month),
        description: (copyForm.description || '').trim(),
      });
      notify.success('GTC copied as a new version.');
      setCopyOpen(false);
      if (created?.id) navigate(`/apps-gtc-view/dtl/${created.id}`);
    } catch (err) {
      notify.apiError(err, 'copy GTC');
    } finally {
      setSaving(false);
    }
  };

  /* ------------------------------- tree --------------------------------- */

  const nodeLabel = (text, onEdit, title) =>
    editable ? (
      <button
        type='button'
        className='gtcd-node__label'
        title={title}
        onClick={onEdit}
      >
        {text}
      </button>
    ) : (
      <span className='gtcd-node__label gtcd-node__label--static'>{text}</span>
    );

  const chevron = (nodeId, isOpen, what) => (
    <button
      type='button'
      className='gtcd-node__chevron'
      aria-label={isOpen ? `Collapse ${what}` : `Expand ${what}`}
      aria-expanded={isOpen}
      onClick={() => toggleNode(nodeId)}
    >
      <i
        className={isOpen ? 'ri-arrow-down-s-line' : 'ri-arrow-right-s-line'}
        aria-hidden='true'
      />
    </button>
  );

  const renderClause = clause => (
    <div key={clause.id} className='gtcd-node gtcd-node--clause'>
      <span className='gtcd-node__chevron gtcd-node__chevron--leaf'>
        <i className='ri-file-list-3-line' aria-hidden='true' />
      </span>
      {clause.clauseNumber && (
        <span className='gtcd-node__clause-number'>{clause.clauseNumber}</span>
      )}
      {nodeLabel(
        clause.content,
        () => openEdit('clause', clause),
        'Edit clause'
      )}
      {editable && (
        <span className='gtcd-node__actions'>
          <Can action='canUpdate'>
            <ActionButton
              variant='icon'
              size='sm'
              icon='ri-pencil-line'
              label='Edit Clause'
              onClick={() => openEdit('clause', clause)}
            />
          </Can>
          <Can action='canDelete'>
            <ActionButton
              variant='icon'
              size='sm'
              danger
              icon='ri-delete-bin-line'
              label='Delete Clause'
              confirm={{
                title: 'Delete this clause?',
                message:
                  'It will be permanently deleted. This cannot be undone.',
                subject: clause.clauseNumber || clause.content,
                confirmLabel: 'Delete Clause',
              }}
              onClick={() =>
                runDelete(
                  () => gtcApi.deleteClause(clause.id),
                  'Clause deleted.'
                )
              }
            />
          </Can>
        </span>
      )}
    </div>
  );

  const renderArticle = article => {
    const isOpen = expanded.has(article.id);
    const clauses = sortByOrder(article.clauses);
    return (
      <div key={article.id} className='gtcd-group gtcd-group--article'>
        <div className='gtcd-node gtcd-node--article'>
          {chevron(article.id, isOpen, 'article')}
          {nodeLabel(
            articleLabel(article),
            () => openEdit('article', article),
            'Edit article'
          )}
          <span className='gtcd-node__meta'>{clauses.length} clause</span>
          {editable && (
            <span className='gtcd-node__actions'>
              <Can action='canCreate'>
                <ActionButton
                  variant='icon'
                  size='sm'
                  icon='ri-add-line'
                  label='Add Clause'
                  onClick={() => openAdd('clause', article.id, clauses)}
                />
              </Can>
              <Can action='canUpdate'>
                <ActionButton
                  variant='icon'
                  size='sm'
                  icon='ri-pencil-line'
                  label='Edit Article'
                  onClick={() => openEdit('article', article)}
                />
              </Can>
              <Can action='canDelete'>
                <ActionButton
                  variant='icon'
                  size='sm'
                  danger
                  icon='ri-delete-bin-line'
                  label='Delete Article'
                  confirm={{
                    title: 'Delete this article?',
                    message:
                      'All clauses under it will also be deleted. This cannot be undone.',
                    subject: articleLabel(article),
                    confirmLabel: 'Delete Article',
                  }}
                  onClick={() =>
                    runDelete(
                      () => gtcApi.deleteArticle(article.id),
                      'Article and its clauses deleted.'
                    )
                  }
                />
              </Can>
            </span>
          )}
        </div>
        {isOpen && (
          <div className='gtcd-children'>
            {clauses.length === 0 ? (
              <div className='gtcd-empty-inline'>
                No clauses in this article yet.
              </div>
            ) : (
              clauses.map(renderClause)
            )}
          </div>
        )}
      </div>
    );
  };

  const renderCategory = category => {
    const isOpen = expanded.has(category.id);
    const articles = sortByOrder(category.articles);
    return (
      <div key={category.id} className='gtcd-group gtcd-group--category'>
        <div className='gtcd-node gtcd-node--category'>
          {chevron(category.id, isOpen, 'category')}
          {nodeLabel(
            category.name,
            () => openEdit('category', category),
            'Edit category'
          )}
          <span className='gtcd-node__meta'>{articles.length} article</span>
          {editable && (
            <span className='gtcd-node__actions'>
              <Can action='canCreate'>
                <ActionButton
                  variant='icon'
                  size='sm'
                  icon='ri-add-line'
                  label='Add Article'
                  onClick={() => openAdd('article', category.id, articles)}
                />
              </Can>
              <Can action='canUpdate'>
                <ActionButton
                  variant='icon'
                  size='sm'
                  icon='ri-pencil-line'
                  label='Edit Category'
                  onClick={() => openEdit('category', category)}
                />
              </Can>
              <Can action='canDelete'>
                <ActionButton
                  variant='icon'
                  size='sm'
                  danger
                  icon='ri-delete-bin-line'
                  label='Delete Category'
                  confirm={{
                    title: 'Delete this category?',
                    message:
                      'All articles and clauses under it will also be deleted. This cannot be undone.',
                    subject: category.name,
                    confirmLabel: 'Delete Category',
                  }}
                  onClick={() =>
                    runDelete(
                      () => gtcApi.deleteCategory(category.id),
                      'Category and its articles and clauses deleted.'
                    )
                  }
                />
              </Can>
            </span>
          )}
        </div>
        {isOpen && (
          <div className='gtcd-children'>
            {articles.length === 0 ? (
              <div className='gtcd-empty-inline'>
                No articles in this category yet.
              </div>
            ) : (
              articles.map(renderArticle)
            )}
          </div>
        )}
      </div>
    );
  };

  /* ------------------------------- render ------------------------------- */

  const meta = MODAL_META[modalType];
  const label = publishLabel(gtc);
  const activeLabel = gtc?.isActive ? 'Active' : 'Inactive';

  const stateButtons = gtc && (
    <Can action='canUpdate'>
      {status !== STATUS_PUBLISH.PUBLISH && (
        <button
          type='button'
          className='gtcd-publish__btn'
          onClick={handlePublish}
        >
          <i className='ri-send-plane-line' aria-hidden='true' />
          Publish to Registered Supplier
        </button>
      )}
      {status === STATUS_PUBLISH.PUBLISH && (
        <button
          type='button'
          className='gtcd-publish__btn gtcd-publish__btn--warning'
          onClick={handleUnpublish}
        >
          <i className='ri-eye-off-line' aria-hidden='true' />
          Unpublish
        </button>
      )}
      {status === STATUS_PUBLISH.UNPUBLISH && (
        <button
          type='button'
          className='gtcd-publish__btn gtcd-publish__btn--muted'
          onClick={handleSetDraft}
        >
          <i className='ri-draft-line' aria-hidden='true' />
          Set to Draft
        </button>
      )}
    </Can>
  );

  let body;
  if (loading && !gtc) {
    body = (
      <div className='to-panel'>
        <SkeletonRows count={4} />
      </div>
    );
  } else if (notFound || !gtc) {
    body = (
      <div className='to-panel'>
        <EmptyState
          title='GTC not found'
          emptyHint='This GTC was not found or is no longer available.'
          action={{
            label: 'Back to GTC List',
            onClick: () => navigate('/apps-gtc-view'),
          }}
        />
      </div>
    );
  } else {
    body = (
      <div className='gtc-stack'>
        {!editable && (
          <div className='alert alert-info d-flex align-items-center gap-2 py-2 mb-0'>
            <i className='ri-lock-line' aria-hidden='true' />
            This GTC is published. Use Copy as New Version to revise it.
          </div>
        )}
        <div className='to-panel'>
          <div className='to-toolbar'>
            <span className='to-toolbar-meta'>
              {tree.length === 0
                ? 'No categories yet'
                : `${tree.length} categor${tree.length === 1 ? 'y' : 'ies'}`}
            </span>
          </div>

          {tree.length === 0 ? (
            <EmptyState
              title='No GTC categories yet'
              emptyHint={
                editable
                  ? 'Add a category (e.g. PART I) to get started'
                  : 'No categories or clauses defined for this GTC document.'
              }
              action={
                editable
                  ? {
                      label: 'Add First Category',
                      onClick: () => openAdd('category'),
                    }
                  : null
              }
            />
          ) : (
            <div className='gtcd-tree'>{tree.map(renderCategory)}</div>
          )}
        </div>

        <div className='gtcd-publish'>
          <div className='gtcd-publish__info'>
            <span className='gtcd-publish__icon'>
              <i className='ri-send-plane-line' aria-hidden='true' />
            </span>
            <div>
              <div className='gtcd-publish__title'>
                Publish GTC to Registered Supplier
                <StatusBadge
                  label={label}
                  tone={PUBLISH_TONE[label]}
                  size='sm'
                />
              </div>
              <div className='gtcd-publish__desc'>
                Set the publish status of GTC ({gtc.title}) for Registered
                Supplier
              </div>
            </div>
          </div>
          <div className='d-flex gap-2 flex-wrap'>{stateButtons}</div>
        </div>
      </div>
    );
  }

  return (
    <React.Fragment>
      <div className='page-content'>
        <Container fluid>
          <div className='to-page email-wrapper d-lg-flex gap-1 mx-n4 mt-n4 p-1'>
            <div className='email-content minimal-border'>
              <div className='p-4'>
                <div className='to-hero mb-4'>
                  <PageBreadcrumb
                    current={gtc?.gtcNumber || gtc?.title || id}
                    fallback={{ label: 'Master GTC', to: '/apps-gtc-view' }}
                  />

                  <div className='dt-header__top'>
                    <DetailBackButton
                      onDark
                      label='Back to GTC List'
                      onClick={() => navigate('/apps-gtc-view')}
                    />
                    {gtc && (
                      <>
                        <StatusBadge
                          label={activeLabel}
                          tone={STATUS_TONE[activeLabel]}
                          size='sm'
                        />
                        <StatusBadge
                          label={label}
                          tone={PUBLISH_TONE[label]}
                          size='sm'
                        />
                        {gtc.sourceGtcId && (
                          <span className='gtc-hero-meta__note'>
                            <i
                              className='ri-file-copy-line'
                              aria-hidden='true'
                            />{' '}
                            Copied from {gtc.sourceGtcNumber || 'another GTC'}
                          </span>
                        )}
                      </>
                    )}
                  </div>

                  <div className='d-flex align-items-center gap-3 flex-wrap'>
                    <span className='to-hero-icon'>
                      <i className='ri-list-check-2' aria-hidden='true' />
                    </span>
                    <div className='flex-grow-1'>
                      <h4 className='to-hero-title'>
                        {gtc ? `${gtc.gtcNumber} — ${gtc.title}` : 'GTC'}
                      </h4>
                      <p className='to-hero-subtitle'>
                        Manage the Category (Part), Article and Clause hierarchy
                        {gtc &&
                          ` · ${monthName(gtc.month)} ${gtc.year}${gtc.incotermName ? ` · ${gtc.incotermName}` : ''}`}
                      </p>
                    </div>
                    <div className='d-flex gap-2 flex-wrap'>
                      <button
                        type='button'
                        className='to-hero-btn'
                        onClick={loadData}
                      >
                        <i className='ri-refresh-line' aria-hidden='true' />
                        Refresh
                      </button>
                      {gtc && (
                        <Can action='canCreate'>
                          <button
                            type='button'
                            className='to-hero-btn'
                            onClick={openCopy}
                          >
                            <i
                              className='ri-file-copy-2-line'
                              aria-hidden='true'
                            />
                            Copy as New Version
                          </button>
                        </Can>
                      )}
                      {editable && (
                        <Can action='canCreate'>
                          <button
                            type='button'
                            className='to-hero-btn to-hero-btn--solid'
                            onClick={() => openAdd('category')}
                          >
                            <i className='ri-add-line' aria-hidden='true' />
                            Add Category / Part
                          </button>
                        </Can>
                      )}
                    </div>
                  </div>
                </div>

                {body}
              </div>
            </div>
          </div>

          {/* Add / Edit — one modal for Category, Article, Clause */}
          <Modal
            isOpen={modalOpen}
            role='dialog'
            autoFocus={true}
            centered
            id='gtcDetailModal'
            toggle={closeModal}
            data-bs-backdrop='static'
            size='md'
            className='gtc-modal'
          >
            <ModalHeader toggle={closeModal} className='gtc-modal__header'>
              <div className='gtc-modal__header-left'>
                <span className='gtc-modal__icon'>
                  <i className={meta.icon} aria-hidden='true' />
                </span>
                <div className='gtc-modal__heading'>
                  <div className='gtc-modal__title'>
                    {editingId ? meta.editTitle : meta.addTitle}
                  </div>
                  <div className='gtc-modal__subtitle'>{meta.subtitle}</div>
                </div>
              </div>
            </ModalHeader>

            <ModalBody>
              <div className='gtc-form'>
                {modalType === 'category' && (
                  <TextField
                    label='Category Name / Part (e.g., PART I - TERMS)'
                    name='name'
                    required
                    placeholder='PART I - TERMS OF GENERAL APPLICATION'
                    value={form.name}
                    error={errors.name}
                    onChange={handleChange}
                  />
                )}
                {modalType === 'article' && (
                  <>
                    <TextField
                      label='Number'
                      name='number'
                      required
                      placeholder='e.g. 1.'
                      value={form.number}
                      error={errors.number}
                      onChange={handleChange}
                    />
                    <TextField
                      label='Article Title'
                      name='title'
                      required
                      placeholder='e.g. Quantity'
                      value={form.title}
                      error={errors.title}
                      onChange={handleChange}
                    />
                  </>
                )}
                {modalType === 'clause' && (
                  <>
                    <TextField
                      label='Clause Number'
                      name='clauseNumber'
                      required
                      placeholder='e.g. 1.1'
                      value={form.clauseNumber}
                      error={errors.clauseNumber}
                      onChange={handleChange}
                    />
                    <TextAreaField
                      label='Clause Content'
                      name='content'
                      required
                      rows={5}
                      placeholder='Enter the full text of the clause…'
                      value={form.content}
                      error={errors.content}
                      onChange={handleChange}
                    />
                  </>
                )}
                <TextField
                  label='Order'
                  name='sortOrder'
                  type='number'
                  required
                  placeholder='1'
                  value={form.sortOrder}
                  error={errors.sortOrder}
                  onChange={handleChange}
                  inputProps={{ min: 1 }}
                />
              </div>
            </ModalBody>

            <ModalFooter>
              <ActionButton
                variant='secondary'
                size='sm'
                icon='ri-close-line'
                onClick={closeModal}
              >
                Cancel
              </ActionButton>
              <ActionButton
                variant='primary'
                size='sm'
                icon='ri-save-line'
                disabled={saving}
                onClick={handleSave}
              >
                {meta.saveLabel}
              </ActionButton>
            </ModalFooter>
          </Modal>

          {/* Copy as New Version */}
          <Modal
            isOpen={copyOpen}
            role='dialog'
            centered
            toggle={() => setCopyOpen(false)}
            data-bs-backdrop='static'
            size='md'
            className='gtc-modal'
          >
            <ModalHeader
              toggle={() => setCopyOpen(false)}
              className='gtc-modal__header'
            >
              <div className='gtc-modal__header-left'>
                <span className='gtc-modal__icon'>
                  <i className='ri-file-copy-2-line' aria-hidden='true' />
                </span>
                <div className='gtc-modal__heading'>
                  <div className='gtc-modal__title'>Copy as New Version</div>
                  <div className='gtc-modal__subtitle'>
                    Copy {gtc?.gtcNumber} with all its content into a new Draft
                    GTC
                  </div>
                </div>
              </div>
            </ModalHeader>
            <ModalBody>
              <div className='gtc-form'>
                <TextField
                  label='GTC Title'
                  name='title'
                  required
                  value={copyForm.title || ''}
                  onChange={e =>
                    setCopyForm(prev => ({ ...prev, title: e.target.value }))
                  }
                />
                <FormGrid columns={2}>
                  <SelectField
                    label='Year'
                    name='copyYear'
                    options={YEAR_OPTIONS}
                    value={copyForm.year}
                    onChange={value =>
                      setCopyForm(prev => ({ ...prev, year: value }))
                    }
                  />
                  <SelectField
                    label='Month'
                    name='copyMonth'
                    options={MONTH_OPTIONS}
                    value={copyForm.month}
                    onChange={value =>
                      setCopyForm(prev => ({ ...prev, month: value }))
                    }
                  />
                </FormGrid>
                <TextAreaField
                  label='GTC Description'
                  name='copyDescription'
                  rows={3}
                  value={copyForm.description || ''}
                  onChange={e =>
                    setCopyForm(prev => ({
                      ...prev,
                      description: e.target.value,
                    }))
                  }
                />
              </div>
            </ModalBody>
            <ModalFooter>
              <ActionButton
                variant='secondary'
                size='sm'
                icon='ri-close-line'
                onClick={() => setCopyOpen(false)}
              >
                Cancel
              </ActionButton>
              <ActionButton
                variant='primary'
                size='sm'
                icon='ri-file-copy-2-line'
                disabled={!String(copyForm.title || '').trim() || saving}
                onClick={handleCopy}
              >
                Copy GTC
              </ActionButton>
            </ModalFooter>
          </Modal>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default GtcDetailView;
